/**
 * ENG-9114 — is "are this integration's tools bound in the session?" even a
 * question worth asking of THIS integration?
 *
 * ## Why this exists
 *
 * `last_session_tool_bind_status` (surfaced as `wired_verdict`) has carried
 * three incompatible meanings in one NULL:
 *
 *   1. never probed yet — transient, resolves on the next session
 *   2. never probed and never will be, because the integration has no MCP
 *      server to bind — correct and permanent
 *   3. probed and something went wrong — which does not actually occur, since a
 *      real failure writes `missing` / `unreachable`
 *
 * Readers assume (3), get (2), and go looking for an error that was never
 * emitted. That cost six days, four agents, two orgs and three superseded
 * root-cause theories on ENG-8751 before anyone opened the log line that said
 * so. Documentation did not fix it: `debug_get_agent_integrations` has said
 * "`null` means never probed, which is NOT 'not wired'" the whole time, and
 * every reader read past it. The field has to carry the distinction.
 *
 * ## What actually decides it
 *
 * The bind probe resolves an integration's `.mcp.json` server key(s) and asks
 * whether the running session holds them. An integration that contributes no
 * `.mcp.json` server has nothing to resolve, so the runner short-circuits and
 * writes nothing — leaving NULL. This module answers the missing half: is that
 * emptiness PERMANENT (not an MCP integration) or TRANSIENT (an MCP
 * integration whose entry has not been rendered yet)?
 *
 * ## The population, measured rather than assumed
 *
 * ENG-9114 framed this as "CLI-delivered (`native` + `cli_binary`)". That is
 * real but it is the minority. On marlow, 8 of 10 rows are NULL:
 *
 *   - `github` — `native` + `cli_binary: 'gh'`, the CLI case the ticket named
 *   - `agt-live`, `elevenlabs`, `image-gen`, `video-gen`, `social-scraping`,
 *     `x-search`, `ninjafy-notes` — `native` with NO `cli_binary`, NO
 *     `mcp_command` and NO `mcp_url`: builtins served through the platform's
 *     own MCP server, never their own `.mcp.json` entry
 *
 * So keying on `cli_binary` alone would have left 7 of 8 still ambiguous.
 * `source_type: 'native'` is a grab-bag of three delivery kinds and cannot be
 * read on its own — the same trap that produced the granola incident, where
 * keying off the raw `source_type` string gave a remote MCP no server key and
 * left it "Connected" with a dead token (see `deriveMcpServerKey`). What
 * separates them is which of `mcp_command` / `mcp_url` / `cli_binary` the
 * toolkit actually sets.
 *
 * ## Parity
 *
 * The rule below is the same predicate, in the same order, as
 * `classifyToolkitSurface` in `packages/api/src/lib/integration-catalog.ts`
 * (`'skill'` is named `'builtin'` here, which is what it means in this
 * context). `apps/cli` cannot import from `packages/api`, so the shared copy
 * lives here where both the host probe and the server can reach it. If one
 * changes, change both — they are answering the same question about the same
 * columns.
 */

/**
 * How an integration reaches the agent.
 *
 *   mcp     - contributes an entry to the agent's `.mcp.json`; the bind probe
 *             can and should answer for it
 *   cli     - delivered as a binary on PATH (`gh`, `aws`, `gcloud`); no MCP
 *             server, so "bound" is not a meaningful question
 *   builtin - served in-process / through the platform's own MCP server; same
 *             conclusion, different reason
 */
export type BindDelivery = 'mcp' | 'cli' | 'builtin';

/**
 * ENG-9477 — which health check answers for each delivery kind.
 *
 * The point of this map is the `satisfies` on the end: add a fourth kind to
 * {@link BindDelivery} and the COMPILER refuses the build until someone writes
 * down what checks it. That is the whole lesson of this ticket. `builtin` was
 * added by ENG-9114 with a correct verdict (`not_applicable` — it has no
 * `.mcp.json` server and never will), and nothing anywhere recorded that a
 * different question still needed asking. Seven integrations then had no
 * tool-level monitoring for months, and the detection mechanism was a customer
 * noticing a stale page (CS-1613).
 *
 * `'none'` is a legitimate value but it has to be CHOSEN. `cli` carries it
 * because a binary on PATH is not session-scoped: it is either installed or it
 * is not, and `.mcp.json` was never going to say so.
 */
export const BIND_DELIVERY_HEALTH_CHECK = {
  /** The session-tool-bind probe resolves its `.mcp.json` server key(s). */
  mcp: 'session-tool-bind',
  /** Deliberately unchecked: a PATH binary is not a per-session fact. */
  cli: 'none',
  /**
   * The forwarded-tool registration report the `augmented` MCP server writes at
   * startup — see `forwarded-tools-report.ts`. This is the entry that did not
   * exist, and its absence is what ENG-9477 is about.
   */
  builtin: 'forwarded-tools-report',
} as const satisfies Record<BindDelivery, string>;

/** The toolkit columns that decide delivery. All optional: absent reads as unset. */
export interface BindDeliveryInput {
  /** `toolkit_definitions.source_type` — `managed | mcp_server | cli_tool | native`. */
  sourceType?: string | null;
  /** `toolkit_definitions.provider` — set for managed (Composio/Pipedream) toolkits. */
  provider?: string | null;
  /** `toolkit_definitions.mcp_command` — a native STDIO MCP server (xero, postiz). */
  mcpCommand?: string | null;
  /** `toolkit_definitions.mcp_url` — a native remote MCP server (granola). */
  mcpUrl?: string | null;
  /** `toolkit_definitions.cli_binary` — the binary a CLI toolkit shells out to. */
  cliBinary?: string | null;
  /** `toolkit_definitions.cli_package` — the package a CLI toolkit installs from. */
  cliPackage?: string | null;
}

/**
 * Classify how an integration is delivered.
 *
 * Order matters and mirrors `classifyToolkitSurface`: a managed toolkit
 * converges into an MCP server whatever its other columns say, so `provider`
 * is checked before anything else; an explicit `mcp_command` / `mcp_url` beats
 * a `source_type` that says `native`; and only once no MCP signal is present
 * does a CLI column get to decide. Everything left is a builtin.
 */
export function classifyBindDelivery(input: BindDeliveryInput): BindDelivery {
  if (input.provider) return 'mcp';
  if (input.mcpUrl || input.mcpCommand) return 'mcp';
  if (input.sourceType === 'mcp_server') return 'mcp';
  if (input.sourceType === 'managed') return 'mcp';
  if (input.cliPackage || input.cliBinary || input.sourceType === 'cli_tool') return 'cli';
  return 'builtin';
}

/**
 * True when the session-tool-bind probe can meaningfully answer for this
 * integration — i.e. it contributes an `.mcp.json` server.
 *
 * A `false` here is what turns an empty key resolution from "we have not
 * managed to probe this yet" into `not_applicable`: a permanent, correct,
 * NOT-broken verdict that a reader can act on at a glance.
 */
export function isSessionToolBindCandidate(input: BindDeliveryInput): boolean {
  return classifyBindDelivery(input) === 'mcp';
}
